import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  getTranscript,
  getTranscriptBefore,
  type AgentRow,
  type TranscriptBlock,
  type TranscriptSource,
} from '../transport/api'
import { ApiError } from '../transport/http'
import { RETRY_BASE_MS, delayBeforeRetry, shouldScheduleRetry } from '../transport/reconnect'
import { TerminalSocket as TerminalSocketImpl, CLOSE_VAULT_LOCKED, type TerminalSocket } from '../transport/ws'
import { productForCli } from './agentKinds'
import { blockCacheKey, capBlocks, dropBlocks, readBlocks, writeBlocks } from './blockCache'
import { atMostEvery } from '../shell/atMostEvery'
import { onStreamOpen } from '../servers/commandStream'
import { resetPane, setPushedLiveText, pointPaneAt } from './live/paneStore'
import { usePaneWatch } from './usePaneWatch'
import { useSelectionHold } from './useSelectionHold'
import { tabNotice } from './browserActivity'

/**
 * THE TRANSCRIPT WINDOW SUBSYSTEM — everything that keeps a live, cached,
 * backfillable view of one agent's transcript current, lifted whole out of
 * TranscriptView (M8 cut 3, 2026-09-03).
 *
 * This is the data window (a tail slice with absolute indices, an IndexedDB seed
 * and flush, scroll-back paging) AND the two things that keep it moving: the
 * event-driven re-read while the agent writes, and the headless pty socket that
 * carries the owner's typing and reports the FACT of output. It composes the two
 * seams the campaign already named — usePaneWatch (the transcript↔socket weld) and
 * useSelectionHold (hold the window still while a reader copies) — so the component
 * that renders it is left with nothing but sender resolution and JSX. Every comment
 * moved verbatim; the behaviour is pinned by scrollBack / liveTranscript /
 * recreatedAgent, which mount TranscriptView and exercise this whole subsystem.
 */
/*
 * THE FIRST PAGE — owner, 2026-09-03: Clean View 的 cache buffer 可以開到 500 個 block;
 * 2026-09-11: 每一個 Block 的 max buffer length 增加到一千 (he remembered the 500).
 *
 * Was 80: enough to fill a phone screen, and everything above it arrived by scrolling
 * (BACKFILL_PAGE at a time). The owner reads long conversations and wants the scroll
 * to be there already, so the opening read is the same number the per-conversation
 * cache keeps (blockCache.MAX_BLOCKS) — one number for "how much of a conversation
 * this app holds", instead of a small first bite and a bigger memory. The cache went
 * to 1000 on 09-03 and this read lagged at 500 until 09-11; the two agree again.
 *
 * The cost is real and lives on the FIRST paint of a conversation, not on scrolling:
 * ~1000 blocks instead of ~80 in the opening response. The edge answers `tail`
 * natively with no ceiling below TRANSCRIPT_MAX_KEEP (2000), so this is one bigger
 * read, not more reads — and the cache means the second open of the same
 * conversation is served locally either way.
 */
const TAIL_BLOCKS = 1000
const BACKFILL_PAGE = 120
/**
 * The fastest the transcript is re-read WHILE the agent is writing.
 *
 * Not a poll: it is a ceiling on an event-driven read, and nothing schedules
 * anything while the terminal is quiet. Close enough to feel live as a reply
 * streams in, far enough apart that a thousand output frames cost a handful of
 * requests rather than a thousand.
 */
const LIVE_MS = 1_000
/**
 * A read that FAILED IN TRANSIT is asked again — owner, 2026-10-04: Clean View 停止在某
 * 一個時間點不再更新了. A read used to be attempted exactly once per signal: an answer
 * lost on the way back (a phone returning from a pocket loses the first one more often
 * than not) left the view where it was until the next signal happened to come, and at
 * the end of a turn no further signal does. One shot each, a bounded number of them,
 * started by the failure and stopped by a success — nothing here runs on a healthy view.
 */
const RETRY_AFTER_MS = [2_000, 5_000, 10_000]
/**
 * How long the watch gets to announce a write that a read just found by other means,
 * before it is judged deaf and rebuilt (see `deaf` below).
 */
const WATCH_GRACE_MS = 2_500
/** The least time between two rebuilds of a watch judged deaf. */
const REBUILD_GAP_MS = 20_000

/**
 * The newest thing somebody SAID to the agent in these blocks, or null.
 *
 * NOT A BROWSER TAB'S NOTICE. A notice is a `user` block too, and with "Notify agent" on
 * one lands every time the owner touches the page — so it was routinely the newest user
 * block, and SentEcho, which asks this whether the message just sent has been recorded,
 * got two wrong answers from it (found by the review of the notice's row, 2026-10-12): a
 * short message whose letters occur in a notice's unseen lines — "look", "next", "again"
 * — counted as recorded and was never echoed; and a message recorded ABOVE a notice was
 * never matched, so its echo stayed as a second copy. A notice is nobody speaking.
 */
export function newestSaidTo(blocks: readonly TranscriptBlock[]): string | null {
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const b = blocks[i]
    if (b?.kind === 'user' && b.text?.trim() && !tabNotice(b)) return b.text
  }
  return null
}

export function useTranscriptWindow({
  agent,
  source,
  socketRef,
  onLink,
  pulse,
}: {
  agent: AgentRow
  source: TranscriptSource
  socketRef: { current: TerminalSocket | null }
  onLink?: ((state: 'connecting' | 'attached' | 'closed') => void) | undefined
  /**
   * WHAT THE REST OF THE APP ALREADY KNOWS ABOUT THIS AGENT, as one string that changes
   * when any of it does: the pushed busy switch, and when it last spoke / was last
   * asked (the rail's own facts). They arrive over the account stream, a different
   * path from the file watch on this view's socket — so when the watch is dead, this
   * still moves, and the view re-reads (see `reconcile`).
   */
  pulse?: string | undefined
}) {
  const [blocks, setBlocks] = useState<TranscriptBlock[]>([])
  /*
   * The newest thing the agent SAID that the transcript already holds. The live block
   * compares itself against this by CONTENT, so a message that has finished and landed
   * is never drawn a second time as if it were still being written. Deliberately read
   * off `blocks` and never merged into it: the live text must not touch `blocksRef`,
   * which is what `have` and the IndexedDB cache are computed from.
   */
  const lastAssistantText = useMemo(() => {
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i]
      if (b?.kind === 'assistant' && b.text?.trim()) return b.text
    }
    return null
  }, [blocks])
  /*
   * The newest USER block, for the same reason its assistant twin exists: something
   * parallel to `blocks` needs to know whether the transcript can already speak for
   * itself. SentEcho draws the message the person just sent until the record lands,
   * and this is how it learns that it has (owner, 2026-09-16 — the prompt that was
   * invisible until the agent replied).
   */
  const lastUserText = useMemo(() => newestSaidTo(blocks), [blocks])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const file = useRef<string | null>(null)
  const log = useRef<HTMLDivElement>(null)
  const stick = useRef(true)

  // Poll the transcript. The server only appends when there is no gap, so this can
  // never silently desync — a gap yields `replace`.
  const blocksRef = useRef<TranscriptBlock[]>([])
  /**
   * The window this panel holds, as ABSOLUTE indices into the transcript.
   *
   * `from` is the index of blocksRef[0] and `pending` is how many older blocks exist
   * below it. Before tail-limiting, both were implicit — the client always held
   * everything, so its array length WAS the absolute index and `from` was always 0.
   * It sent that length as `have`, which the server reads as an absolute index. That
   * happened to agree only because the two coincided.
   */
  const from = useRef(0)
  const pending = useRef(0)
  /**
   * The lowest index the edge will serve, when a response has said: `from - pending`
   * on a tail replace or a page of history (the edge keeps only the newest
   * TRANSCRIPT_MAX_KEEP blocks, and `pending` is what it holds below `from`). Null
   * until one has — an append carries no `pending`, and a window seeded from the cache
   * has never been told — because 0 there would read as "nothing below", the very
   * trap that turned history off once before (see `atStart`).
   *
   * Read by `reach` alone: a search match older than this cannot be loaded, and paging
   * down to find that out cost the owner's phone a thousand blocks it then held for
   * nothing (2026-09-28, a keyword whose newest match was 10,800 blocks back).
   */
  const floor = useRef<number | null>(null)
  /**
   * What the SOURCE says, for the footer.
   *
   *   owner, 2026-08-07: blocks count 一直在跳. 這不應該是從 source 先測量到的數值嗎?
   *
   * They are right, and the old footer answered a different question: it printed
   * `blocks.length`, which is how many blocks this component happens to be holding.
   * That grows with every append while an agent works and jumps by a page on every
   * scroll-back, so it moves for reasons a reader cannot see and never means what
   * "N blocks" sounds like it means. `total` is in every response and IS a measurement
   * of the transcript.
   *
   * STATE rather than a ref. I first wrote that this fixes a stale "…N earlier" line,
   * and mutation testing said otherwise: reverting that line to `pending.current` keeps
   * every test green, because backfill always sets the ref immediately before a
   * setBlocks that re-renders — so the ref is never read stale in practice. The reason
   * that survives is narrower and worth stating honestly: one value, read the same way
   * in both places, instead of a ref and a state that can be updated from different
   * branches. `total` is the part that genuinely needs state; `pending` rides along so
   * the two cannot come from different responses.
   */
  const [meta, setMeta] = useState<{
    total: number
    pending: number
    from: number
    end: boolean
  }>({ total: 0, pending: 0, from: 0, end: false })
  /**
   * Set only when the server has actually said "there is nothing older".
   *
   * Scroll-back used to be gated on `pending > 0`, and `pending` is an OPTIONAL field
   * that append responses do not carry at all — so one missing number turned history
   * off permanently, with no error and nothing on screen to say why. This is a fact we
   * observed instead: a page for this same file came back empty.
   */
  const atStart = useRef(false)
  const backfilling = useRef(false)
  /** Scroll height captured before a prepend, so the view can stay where it was. */
  const anchor = useRef<number | null>(null)
  /*
   * The selection latch — hold the conversation still while a reader copies out of
   * it, queue what arrives, flush on release. Lifted whole into useSelectionHold
   * (M8 cut 5): the window engine below reads `holding` and writes `queued` through
   * these refs and calls `resetHold` when the conversation switches.
   */
  const { holding, queued, resetHold, held, release: releaseHold, park } = useSelectionHold({ log, setBlocks })
  /**
   * Which load this window belongs to.
   *
   * `tick` closes over an `alive` flag and checks it; `backfill` is called from the
   * scroll handler, outside that closure, and had no equivalent — so a page already in
   * flight when the effect re-ran (an agent switch, a view toggle) prepended itself onto
   * the window that replaced it, and set `from`/`pending` to describe the conversation
   * it came from. The count then reads one page instead of window+page, and the next
   * scroll asks from an index that belongs to nothing on screen: the owner's "traces
   * back a few blocks and then stops".
   *
   * A counter rather than a boolean because the same ref has to survive the reset — the
   * point is to tell WHICH load a reply belongs to, not whether some load is current.
   */
  const gen = useRef(0)
  /*
   * The transcript↔socket seam — `activity`, `watchArmed`, `unborn` and the arm call —
   * lives in usePaneWatch (M8 cut 1). Four refs that BOTH effects below used to reach
   * into, with no owner and no stated contract; that file holds them and the measured
   * history behind each one, verbatim.
   */
  const {
    activity: watchActivity,
    unborn: watchUnborn,
    watchArmed,
    arm: armWatch,
  } = usePaneWatch({ session: agent.session, source })

  /** The effect owns this; backfill is defined outside it and needs the same path. */
  const apply = useRef<(next: TranscriptBlock[]) => void>(() => {})
  /** Re-read now, for a reason that is not the watch — owned by the effect, like `apply`. */
  const reconcile = useRef<() => void>(() => {})

  useEffect(() => {
    // Bumped, not captured: `backfill` compares against gen.current at both ends of
    // its await, and `tick` already has `alive`.
    gen.current++
    let alive = true
    file.current = null
    blocksRef.current = []
    from.current = 0
    pending.current = 0
    floor.current = null
    backfilling.current = false
    anchor.current = null
    atStart.current = false
    setMeta({ total: 0, pending: 0, from: 0, end: false })
    // A held selection cannot survive the conversation switching under it — the DOM
    // it was in is gone — so the hold is cleared with everything else (see
    // useSelectionHold's resetHold for why a surviving hold would freeze the new one).
    resetHold()
    setBlocks([])
    setLoading(true)
    setError(null)

    apply.current = (next: TranscriptBlock[]) => {
      blocksRef.current = next
      // The ref is the truth for the next poll's `have` either way — only the render
      // waits. Otherwise a held selection would make the client re-request blocks it
      // already has, and the answer would be a replace, which is worse than a repaint.
      if (holding.current) park(next)
      else setBlocks(next)
    }

    /*
     * SEED FROM THE DEVICE BEFORE THE FIRST REQUEST — owner, 2026-08-22: Clean View 的
     * Cache 可以在 Device 上面儲存 500 個最新 Chat blocks，這樣每次重刷就不需要重讀
     * 一整串了.
     *
     * Two wins, and the second is the larger one. The obvious one is paint: measured on
     * app-dev, opening a 7388-block conversation loaded 81 blocks and the slowest relay
     * call in that open took 206ms, all of it staring at nothing. The other is that a
     * seeded cache turns the FIRST request into a delta — `have` below is real instead
     * of -1, and the file is pinned, so the server appends the handful of blocks that
     * arrived since instead of sending a window.
     *
     * The first tick waits on this rather than racing it: an IndexedDB read is single
     * milliseconds against a round trip, and a tick that started first would ask with
     * `have = -1` and throw the whole point away.
     */
    const ckey = blockCacheKey({
      hash: agent.hash,
      session: agent.session,
      host: agent.host,
      device: agent.device_id,
    })
    const seeded = readBlocks(ckey).then((c) => {
      // `blocksRef.current.length` guards the race in the other direction: if anything
      // real has already landed, the server has spoken and the cache must not touch it.
      if (!alive || !c || !c.blocks.length || blocksRef.current.length) return
      file.current = c.file
      from.current = c.from
      blocksRef.current = c.blocks
      apply.current(c.blocks)
      setMeta((m) => ({ ...m, total: c.total, from: c.from }))
      setLoading(false)
    })

    /*
     * THROTTLED, because the write is the expensive half and it sits on the poll.
     *
     * `writeBlocks` serialises the whole window — up to blockCache.MAX_BLOCKS (1000)
     * blocks, on the order of 4MB — and a tick carries new blocks every couple of seconds while an agent is
     * actually talking. Storing that on every one of them is a cost that grows with the
     * cap, on the hot path, which is the shape avg-latency spent 2026-08-22 catching in
     * three other places. A window of five seconds costs at most one write per five
     * seconds of speech, and the flush below means nothing is lost by waiting.
     */
    /** What the server last said the conversation's length was. */
    let lastTotal = 0
    const saveCache = atMostEvery(5_000, () => {
      if (!blocksRef.current.length || !file.current) return
      void writeBlocks(ckey, {
        file: file.current,
        ...capBlocks(from.current, blocksRef.current),
        total: lastTotal || blocksRef.current.length,
        at: Date.now(),
      })
    })

    /*
     * ONE READ AT A TIME. Every trigger below calls `tick`, and there are now several of
     * them; two reads in flight would both be answered from the same `have` and the
     * second append would draw the same blocks twice. A trigger that lands mid-read is
     * remembered and served the moment the read ends, so nothing it announced is lost.
     */
    let inFlight = false
    let again = false
    let failures = 0
    let retry: ReturnType<typeof setTimeout> | null = null
    /** Whether the read in flight was started by the watch itself. */
    let byWatch = false
    let nextByWatch = false
    let lastActivity = 0
    let lastRebuild = 0
    let grace: ReturnType<typeof setTimeout> | null = null
    /*
     * THE WATCH IS JUDGED BY WHAT IT FAILED TO SAY. A read started by something else
     * (the pushed presence, a return to the app, the stream reopening, a retry) that
     * comes back WITH new blocks means the file was written — and the watch exists to
     * announce exactly that. If it still has not spoken a moment later, it is deaf:
     * its socket is a stream the machine no longer has, or a watch that was never
     * armed. Closing the socket hands it to the reconnect that already knows how to
     * open, attach and arm a fresh one. Evidence-driven and rate-limited; a healthy
     * watch speaks within milliseconds and this never fires.
     */
    const deaf = (since: number) => {
      if (grace !== null) return
      // Already announced, a moment before this read began: the watch is fine and the
      // read merely got there first (the end of a turn — its last write and the busy
      // switch flipping arrive together). Judging it deaf here rebuilt a healthy socket
      // at the end of every turn.
      if (lastActivity >= since - WATCH_GRACE_MS) return
      grace = setTimeout(() => {
        grace = null
        if (!alive || lastActivity >= since - WATCH_GRACE_MS) return
        if (Date.now() - lastRebuild < REBUILD_GAP_MS) return
        lastRebuild = Date.now()
        watchArmed.current = { sock: null, file: null }
        socketRef.current?.close()
      }, WATCH_GRACE_MS)
    }

    const tick = async () => {
      if (inFlight) {
        again = true
        return
      }
      inFlight = true
      byWatch = nextByWatch
      nextByWatch = false
      const started = Date.now()
      let lost = false
      if (retry !== null) {
        clearTimeout(retry)
        retry = null
      }
      try {
        await seeded
        if (!alive) return
        // Absolute, not the array length: with a tail window the client's first block
        // is at `from`, so its end is from + length. Sending the length would name a
        // much earlier index and the server would answer by re-sending everything
        // between — silently undoing the tail on the very next poll.
        const have = file.current === null ? -1 : from.current + blocksRef.current.length
        const t = await getTranscript(
          agent.session, have, agent.host, source, TAIL_BLOCKS, file.current,
        )
        if (!alive) return
        /*
         * WHAT THE RESPONSE ACTUALLY SAID, recorded before any branch decides what to
         * do with it. The per-branch versions of this disagreed about when to run, so
         * a conversation that opened on the wrong branch showed nothing at all — and
         * an absent number is indistinguishable from a zero one on screen.
         *
         * NOT `from`. In an append, the edge's `from` is where the NEW blocks start
         * (`from: clientHave`), not where this window starts — and `meta.from` is read
         * as the window's start: the "…earlier — tap or scroll up to load" button and
         * the search's index-to-block mapping. Recorded here, the first append turned
         * a conversation loaded from block 0 into one that promised history it did not
         * have, and moved every search match onto the wrong block (2026-09-28: a
         * global-search tap landed on nothing). The window's start moves only where
         * `from.current` moves — replace, file change, backfill, cache — and each of
         * those sets `meta.from` itself.
         */
        // The same object when nothing moved: this runs on every re-read (about once a second while the agent writes),
        // and a new object re-rendered the whole view each time (owner, 2026-11-04: UI 有時候會變得很卡).
        setMeta((m) => {
          const total = typeof t.total === 'number' ? t.total : m.total
          const pending = typeof t.pending === 'number' ? t.pending : m.pending
          return total === m.total && pending === m.pending ? m : { ...m, total, pending }
        })
        if (t.file !== file.current) {
          // A different transcript file means a different conversation: reset rather
          // than appending into the wrong one.
          file.current = t.file
          from.current = t.from
          pending.current = t.pending ?? 0
          floor.current = typeof t.pending === 'number' ? t.from - t.pending : null
          atStart.current = false
          setMeta((m) => ({ ...m, total: t.total || m.total, from: t.from,
            pending: t.pending ?? 0, end: false }))
          apply.current(t.blocks)
        } else if (t.mode === 'append') {
          if (t.blocks.length && t.from !== from.current + blocksRef.current.length) {
            // An answer for a position this window is no longer at. Ask again from here
            // rather than draw blocks twice or leave a hole.
            again = true
          } else if (t.blocks.length) {
            apply.current([...blocksRef.current, ...t.blocks])
            setMeta((m) => (m.total === t.total ? m : { ...m, total: t.total }))
            if (!byWatch) deaf(started)
          }
        } else {
          // A replace is the server saying the client's window no longer lines up, so
          // any history backfilled below it is gone too. Rare — it needs the file to
          // change or `have` to fall outside the window — and the alternative is
          // stitching a new window onto an old one across a gap the server just said
          // it could not bridge.
          from.current = t.from
          pending.current = t.pending ?? 0
          floor.current = typeof t.pending === 'number' ? t.from - t.pending : null
          atStart.current = false
          setMeta((m) => ({ ...m, total: t.total || m.total, from: t.from,
            pending: t.pending ?? 0, end: false }))
          apply.current(t.blocks)
        }
        setError(null)
        failures = 0
        // The file is known (and may just have rotated): arm or re-arm the watch.
        watchUnborn.current = false
        armWatch({
          socket: () => socketRef.current,
          file: () => file.current,
          have: () => (file.current === null ? -1 : from.current + blocksRef.current.length),
        })
        /*
         * KEEP WHAT THIS DEVICE NOW HOLDS. Only when the answer moved something — the
         * poll ticks whether or not anything was said, and rewriting an identical
         * window every couple of seconds is a cost with no reader.
         *
         * Written from the refs rather than from `t`, because backfilled history is in
         * them too and `capBlocks` keeps the newest MAX_BLOCKS (1000) of whatever is there.
         */
        lastTotal = typeof t.total === 'number' ? t.total : lastTotal
        if (t.blocks.length) saveCache()
      } catch (e) {
        /*
         * Classify by STATUS and body, never by looking for "404" inside the
         * stringified error. That substring test read the server's prose, which is
         * the server's to change, and would fire on any message that merely
         * contained those three digits — a path, a port, a byte count.
         *
         * It also flattened states the backend goes to some trouble to separate.
         * A pin whose file has vanished (409 stale_mapping) is not "nothing here
         * yet": it is the one case the user can act on, and it arrives with an
         * explanation worth showing verbatim instead of as "ApiError: …".
         */
        if (!alive) return
        if (e instanceof ApiError) {
          const body = (e.body ?? {}) as { state?: string; error?: string }
          /*
           * THE SEEDED CACHE IS NOW DISPROVEN. Everything below this line is the
           * server saying, one way or another, that it will not vouch for a
           * conversation under this agent's name — nothing written (404), a pin
           * whose file is gone (stale_mapping), or a refusal to guess between
           * neighbours (unpinned_shared_cwd).
           *
           * Blocks on screen at this moment can only have come from the device
           * cache, because a successful read would have replaced them. That cache
           * is keyed by the agent hash, which is derived from the session NAME —
           * so a deleted-and-recreated `probe1` seeds from the previous occupant.
           * Leaving them up renders one agent's messages under another's name,
           * beneath a line that says there is no transcript.
           *
           * Deliberately NOT every failure: a network blip or a 500 must leave the
           * cache alone, or the paint win is spent on the first bad tick. Only an
           * answer that is ABOUT the conversation's existence counts as proof.
           */
          if (body.state || e.status === 404) {
            file.current = null
            from.current = 0
            floor.current = null
            blocksRef.current = []
            apply.current([])
            setMeta((m) => ({ ...m, total: 0, from: 0, pending: 0, end: false }))
            void dropBlocks(ckey)
            // Nothing exists yet: ask the edge to announce the file's birth — now,
            // and again from onOpen if the socket is not ready yet.
            if (e.status === 404) {
              watchUnborn.current = true
              armWatch({
          socket: () => socketRef.current,
          file: () => file.current,
          have: () => (file.current === null ? -1 : from.current + blocksRef.current.length),
        })
            }
          }
          setError(
            /*
             * ANY classified state, not only stale_mapping. The server puts a
             * `state` on a response when it went to the trouble of explaining
             * something a person can act on — 2026-08-20 added
             * unpinned_shared_cwd, the refusal that replaced silently showing a
             * neighbour's conversation under this agent's name. A list of
             * states here would be a second place to forget, and the message
             * it carries is the whole point of the classification.
             */
            body.state
              ? (body.error ?? e.message)
              : e.status === 404
                ? /*
                   * Two words removed on purpose. "agy" was the binary name in a
                   * sentence a person reads — Antigravity is what they picked. And
                   * "yet" claimed a cause this response cannot support: a 404 here
                   * covers both "nothing written so far" and "this server has no
                   * reader for that source at all", and only the first one gets
                   * better by waiting. Observed as the second: `source=agy` fell
                   * through _resolve_source's default and 404'd as Claude.
                   */
                  `No ${productForCli(source)} transcript for this session.`
                : e.message,
          )
          if (!body.state && e.status !== 404) lost = true
        } else {
          setError(String(e))
          lost = true
        }
      } finally {
        inFlight = false
        if (alive) {
          setLoading(false)
          if (again) {
            again = false
            void tick()
          } else if (lost && failures < RETRY_AFTER_MS.length && document.visibilityState === 'visible') {
            // The answer was lost, not refused: ask again, a bounded number of times.
            // Hidden: not at all — coming back is itself a read (onVisible below).
            retry = setTimeout(() => {
              retry = null
              if (alive) void tick()
            }, RETRY_AFTER_MS[failures])
            failures += 1
          }
        }
      }
    }

    void tick()
    reconcile.current = () => {
      if (alive) void tick()
    }
    /*
     * THE ACCOUNT STREAM CAME BACK. Whatever the watch announced while that pipe was
     * down never arrives late — it never arrives — so its reopening is a reason to read,
     * the same bell every other consumer of the stream already answers.
     */
    const offStreamOpen = onStreamOpen(() => {
      if (alive) void tick()
    })

    /*
     * RE-READ WHEN THE AGENT WRITES — owner, 2026-08-10: clean view 的更新好像也很
     * lag, 非常不即時.
     *
     * The two-second poll was removed under the no-heartbeat rule and the comment
     * that replaced it promised "live updates belong to a pushed transcript event".
     * That event was never built, so what shipped reads the transcript on open, on
     * these deps, and on window focus — and nothing else. Sit and watch a working
     * agent and the conversation simply does not move; the owner has to leave the app
     * and come back to see what it said. A promise of a future mechanism is not a
     * mechanism.
     *
     * The event already exists on the wire, and it is the same one the busy dot uses:
     * a working pane repaints, and its transcript is what those repaints are being
     * written into. So this is edge-triggered on OUTPUT, never on a clock — nothing
     * fires while the terminal is quiet, which is the whole of the rule.
     *
     * Throttled, not debounced: a streaming reply must APPEAR while it streams, so a
     * trailing-only read would land after the answer was already finished. One read
     * per LIVE_MS while output flows (the ignore-while-pending guard is what keeps a
     * thousand frames from becoming a thousand requests), and the last scheduled read
     * lands after the writing stops, which is what catches the tail.
     */
    let live: ReturnType<typeof setTimeout> | null = null
    let lastRead = Date.now()
    /*
     * THIS SESSION'S OUTPUT, not the account's. This subscribed to the global
     * pty bus and filtered on nothing but `op === 'out'`, so ANY stream's bytes
     * — another agent in another tab, a terminal on another machine — re-read
     * THIS transcript once a second for as long as they flowed. One busy agent
     * made every open Clean View on the account poll in sympathy.
     *
     * The headless socket below is attached to exactly this agent's pane, and
     * activity() is the socket saying "my pane wrote" — bytes on an old edge,
     * act pings on one that mutes. Same throttle, same tail-catch; the only
     * thing that changed is WHOSE writing can cause a read.
     */
    watchActivity.current = () => {
      lastActivity = Date.now()
      if (!alive || live) return
      live = setTimeout(
        () => {
          live = null
          lastRead = Date.now()
          nextByWatch = true
          if (alive) void tick()
        },
        Math.max(0, LIVE_MS - (Date.now() - lastRead)),
      )
    }

    /*
     * RECONCILE ON RETURN. A phone in a pocket drops every socket; the signals
     * that fired while it was away are gone, and a signal that was missed never
     * arrives late — it never arrives. This was the only view without a
     * foreground re-read (tick ran on mount and on activity, nothing else), so a
     * conversation that moved while the app was backgrounded stayed at its
     * last frame until the next signal happened to come. One read on visible;
     * not a clock — nothing fires while the app sits in front.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible' && alive) void tick()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      alive = false
      watchActivity.current = null
      reconcile.current = () => {}
      offStreamOpen()
      document.removeEventListener('visibilitychange', onVisible)
      if (live) clearTimeout(live)
      if (retry !== null) clearTimeout(retry)
      if (grace !== null) clearTimeout(grace)
      /*
       * FLUSH ON THE WAY OUT. The save is throttled, so leaving inside a window would
       * drop whatever arrived in it — and leaving is exactly when the cache matters,
       * since the next thing that happens is the person coming back.
       */
      if (blocksRef.current.length && file.current) {
        void writeBlocks(ckey, {
          file: file.current,
          ...capBlocks(from.current, blocksRef.current),
          total: lastTotal || blocksRef.current.length,
          at: Date.now(),
        })
      }
    }
    /*
     * agent.device_id AND agent.hash belong here — the M8 audit confirmed a real
     * defect: two agents can share a name and a host on DIFFERENT machines
     * (settleRoster's "web-train-board exists on two machines today"), and without
     * these the effect did not re-run on that switch. The window kept the previous
     * machine's blocks under the new agent's name, and the IndexedDB key stayed
     * pointed at the old device. It healed only via the file-change branch on a
     * later tick.
     *
     * armWatch/watchActivity/watchUnborn are stable by construction (a useCallback
     * over two strings, and refs) — listed honestly rather than suppressed, which is
     * what the ratchet is for. socketRef is a ref object; likewise. holding/queued
     * (useSelectionHold's refs) and resetHold (its useCallback over nothing) are the
     * same shape and listed for the same reason.
     */
  }, [
    agent.session,
    agent.host,
    agent.device_id,
    agent.hash,
    source,
    armWatch,
    watchActivity,
    watchUnborn,
    watchArmed,
    socketRef,
    holding,
    queued,
    resetHold,
    park,
  ])

  /*
   * THE REST OF THE APP SAW THIS AGENT MOVE — owner, 2026-10-04: Clean View 跟不上
   * Terminal 的內容. The rail's busy dot and "last said" arrive over the account stream
   * and were live the whole time the conversation stood still; they are the same facts
   * this view shows, by a path that does not pass through its socket or its watch. So
   * when they change, read. Costs one request per turn boundary on a healthy view, and
   * is what brings a view with a dead watch back in step — the read's own result then
   * exposes the dead watch (`deaf`) and has it rebuilt.
   */
  const pulsed = useRef(pulse)
  useEffect(() => {
    if (pulsed.current === pulse) return
    pulsed.current = pulse
    reconcile.current()
  }, [pulse])

  /**
   * Read now and show everything, on the reader's own say-so — the footer's tap. Ends a
   * selection hold, re-arms the watch on the next read, and asks. Nothing it does is
   * wrong on a healthy view, so it needs no knowledge of what, if anything, was stuck.
   */
  const refresh = () => {
    releaseHold()
    watchArmed.current = { sock: null, file: null }
    reconcile.current()
  }

  /**
   * Fetch the page of history below the window and put it on top.
   *
   * Guarded by a ref rather than state: the trigger is a scroll handler, which fires
   * far faster than a round trip, and a second request would ask for the same page
   * and prepend it twice.
   */
  const backfill = async () => {
    // A prepend moves every line on screen, so it is the one thing a reader who is
    // mid-selection least wants. The trigger fires again on the next scroll.
    if (holding.current) return
    if (backfilling.current || atStart.current || file.current === null) return
    // Nothing above index 0 by definition; everything else is the server's to answer.
    if (from.current <= 0) return
    // Which window asked. Compared again after the await — see `gen`.
    const asked = gen.current
    backfilling.current = true
    try {
      const t = await getTranscriptBefore(
        agent.session, from.current, BACKFILL_PAGE, agent.host, source, file.current,
      )
      setMeta((m) => ({
        ...m,
        total: typeof t.total === 'number' ? t.total : m.total,
        pending: typeof t.pending === 'number' ? t.pending : m.pending,
      }))
      /*
       * The window that asked for this page is gone. Not merely "do not render it" —
       * `from` and `pending` must not move either, because they describe a conversation
       * this component is no longer showing.
       *
       * The file check below cannot stand in for this. Re-running the effect on the
       * SAME conversation — toggling the view, which is one tap — resets the window
       * while the file stays identical, so the page passes every check and lands
       * anyway.
       */
      if (asked !== gen.current) return
      // The conversation changed under us; this page belongs to a different file.
      if (t.file !== file.current || !t.blocks.length) {
        // Same file and no blocks IS the end of the history the server will serve.
        // A different file is someone else's page and says nothing about ours.
        if (t.file === file.current) {
          atStart.current = true
          pending.current = 0
          setMeta((m) => ({ ...m, pending: 0, end: true }))
        }
        return
      }
      const el = log.current
      anchor.current = el ? el.scrollHeight - el.scrollTop : null
      from.current = t.from
      pending.current = t.pending ?? 0
      if (typeof t.pending === 'number') floor.current = t.from - t.pending
      setMeta((m) => ({
        ...m,
        from: t.from,
        total: t.total || m.total,
        pending: typeof t.pending === 'number' ? t.pending : m.pending,
      }))
      /*
       * Through `apply`, not a bare setBlocks. A selection can BEGIN during the round
       * trip — the guard at the top only sees the moment the request went out — and a
       * prepend moves every line, which collapses it. apply() queues instead, and the
       * page lands the moment the selection is released.
       */
      apply.current([...t.blocks, ...blocksRef.current])
    } catch {
      // Leave `pending` alone so scrolling up again retries. Failing to load history
      // is not a reason to claim there is none.
    } finally {
      backfilling.current = false
    }
  }

  /**
   * LOAD OLDER BLOCKS UNTIL BLOCK `index` IS IN THE WINDOW — the search's "go to that
   * match" (owner, 2026-09-28: the Agent Session's search asks the edge, which searches
   * the whole conversation, so a match can sit far above what is loaded). True once it
   * is loaded; false when the window cannot reach back that far — the edge serves only
   * the newest blocks of a long conversation, and a match older than that cannot be
   * drawn here at all (the caller shows its text instead). Page by page through the one
   * backfill there is, so the scroll anchoring and the generation guard still apply.
   */
  const reach = async (index: number): Promise<boolean> => {
    for (let pages = 0; pages < 200; pages++) {
      if (from.current <= index) return true
      if (atStart.current || from.current <= 0 || file.current === null) return false
      // Below what the edge serves: no page will ever hold it, so load none (see `floor`).
      if (floor.current !== null && index < floor.current) return false
      const was = from.current
      await backfill()
      if (from.current === was) {
        // Another backfill is in flight (a scroll-up got there first): let it land.
        if (backfilling.current) {
          await new Promise((r) => setTimeout(r, 60))
          continue
        }
        return from.current <= index
      }
    }
    return from.current <= index
  }
  useLayoutEffect(() => {
    const el = log.current
    if (!el) return
    if (anchor.current !== null) {
      // Older blocks went in above the viewport, so every offset moved down by
      // exactly the height they added. Restoring the distance from the BOTTOM keeps
      // the reader on the line they were looking at; setting scrollTop back to its
      // old value would jump them into the middle of the page just inserted.
      el.scrollTop = el.scrollHeight - anchor.current
      anchor.current = null
      return
    }
    if (stick.current) el.scrollTop = el.scrollHeight
  }, [blocks])


  // Even in a transcript view the owner's input must go through the pty — that is
  // the channel a human's typing belongs on. So attach a headless socket: input
  // flows down it, and the only thing that comes back up is the FACT of output
  // (quiet:true — the machine replaces this pane's bytes with act pings).
  useEffect(() => {
    /*
     * It RECONNECTS, which it did not before.
     *
     * This socket used to report its close and stop. The terminal view has always
     * retried, so a drop there healed itself and a drop here did not — and nothing on
     * screen distinguishes the two, because a transcript renders from HTTP and goes on
     * looking perfectly alive. The result was Clean View that could no longer send
     * anything until the component happened to remount, which is the owner's report:
     * sometimes text will not go, and the terminal behind it is closed.
     *
     * The server restarted four times in one afternoon while this was being built, so
     * this is not a rare path.
     */
    let disposed = false
    let refused = false
    let delay = RETRY_BASE_MS
    let timer: number | null = null

    /*
     * Point the pane store at THIS conversation, and give it the way to ask for a
     * capture. Keyed by (host, session) so one pane's text can never be read as
     * another's — the store drops what it holds the moment the key changes.
     */
    pointPaneAt(`${agent.host ?? ''}/${agent.session}`)

    const open = () => {
      if (disposed) return
      onLink?.('connecting')
      const sock = new TerminalSocketImpl(
        {
          onActivity: () => {
            watchActivity.current?.()
          },
          // The machine's own account of the message being written (codex).
          onLiveText: (text, kind) => {
            if (!disposed) setPushedLiveText(text, kind)
          },
          onOpen: () => {
            if (disposed) return
            delay = RETRY_BASE_MS
            // A fresh pty is a plain login shell, so reach the agent by attaching.
            sock.attach(agent.session)
            onLink?.('attached')
            setError(null)
            // A new socket is a new server-side feed: (re)arm the file watch on
            // it. No-op until the first read has named the file.
            armWatch({
          socket: () => socketRef.current,
          file: () => file.current,
          have: () => (file.current === null ? -1 : from.current + blocksRef.current.length),
        })
          },
          // A server-side refusal is not a transport failure — `host_not_mounted`
          // will not come back on its own, so stop rather than hammer a host that
          // is not there. Same rule the terminal view follows.
          onServerError: (info) => {
            refused = true
            setError(info.message ?? info.error)
          },
          onClose: (ev) => {
            if (disposed) return
            onLink?.('closed')
            if (ev.code === CLOSE_VAULT_LOCKED) setError('Vault locked — the terminal closed.')
            if (refused) return
            schedule()
          },
        },
        agent.host,
        // QUIET: Clean View renders from HTTP; the pane's byte stream was pure
        // freight here — and, fanned out, everyone else's freight too.
        true,
        // Same pinning as the terminal view: a rich view's input socket must
        // reach the machine the AGENT is on, not the one the relay happens to
        // address (owner, 2026-08-28: the second pane's wrong machine).
        agent.device_id ?? null,
      )
      sock.connect()
      socketRef.current = sock
    }

    const schedule = () => {
      if (disposed || timer !== null) return
      // Hidden: stop entirely rather than back off. Coming back is what restarts it,
      // so nothing is lost and a backgrounded phone stops polling.
      if (!shouldScheduleRetry()) return
      void delayBeforeRetry(delay).then((ms) => {
        if (disposed) return
        delay = ms
        timer = window.setTimeout(() => {
          timer = null
          open()
        }, ms)
      })
    }

    open()
    /*
     * Returning to the app reconnects immediately.
     *
     * This is the other half of not scheduling while hidden: without it a socket that
     * dropped in someone's pocket would stay dropped until they typed something.
     */
    const onVisible = () => {
      if (disposed || document.hidden || socketRef.current?.ready) return
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      delay = RETRY_BASE_MS
      open()
    }
    document.addEventListener('visibilitychange', onVisible)

    /** Pressing send while disconnected asks for a retry NOW, rather than waiting. */
    const onKick = () => {
      if (disposed || socketRef.current?.ready) return
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      delay = RETRY_BASE_MS
      open()
    }
    window.addEventListener('wt-agent-socket-kick', onKick)

    return () => {
      disposed = true
      window.removeEventListener('wt-agent-socket-kick', onKick)
      document.removeEventListener('visibilitychange', onVisible)
      if (timer !== null) window.clearTimeout(timer)
      // Leaving drops the pane with it: text captured from the conversation being
      // closed must never be read as the next one's.
      resetPane()
      const cur = socketRef.current
      socketRef.current = null
      cur?.close()
    }
  /*
   * agent.device_id BELONGS HERE because the effect above reads it — it is the third
   * argument to the socket, the one that pins this connection to the machine the AGENT
   * is on rather than whichever machine the relay happens to address.
   *
   * `device_id?: string | undefined` (everywhere.ts:455) is optional and arrives with
   * the roster, so the first render of a URL-restored panel can have session and host
   * and NOT the id. Built that way the socket is relay-addressed; when the id landed a
   * moment later this list did not change, so nothing rebuilt it and the panel kept
   * typing at the wrong machine for as long as it stayed open. That is the same failure
   * the third argument was added to fix (owner, 2026-08-28: the second pane's wrong
   * machine) — the argument was passed and the rebuild was not armed.
   *
   * (Until M6 there was NO linter here, and socketDeps.test.ts hand-scanned this one
   * effect for exactly this. eslint + the exhaustive-deps ratchet now cover the class;
   * that test remains as the named pin for the effect that already cost a
   * wrong-machine bug.)
   */
  }, [agent.session, agent.host, agent.device_id, socketRef, onLink, armWatch, watchActivity])
  return {
    blocks,
    meta,
    loading,
    error,
    backfill,
    reach,
    log,
    stick,
    lastAssistantText,
    lastUserText,
    held,
    refresh,
    /** The transcript file these blocks are from — with a block's absolute index, its address (agents/bookmarks). */
    file: file.current,
  }
}
